import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import * as cdpService from '../../services/CdpService';
import { CDP_EVENTS } from '../../../src/constants';

import { NextImage, RichText } from '@sitecore-jss/sitecore-jss-nextjs';
import lowerCase from 'lodash/lowerCase';
import {
  StRichTextGridItem,
  StTitle,
  StBannerContainer,
  StBannerWrapper,
  StBannerContent,
  StEllipseBox,
  StBoxWrapper,
} from './TAHeroBanner.styles';
import { TAHeroBannerProps } from './TAHeroBanner.types';
import { ButtonType } from '../../lib/type/Button';
// import { useSelector } from 'react-redux';
// import { RootState } from '../../store/store';

import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import ButtonPrimary from '../../components/shared/Button/PrimaryButton';
import ButtonSecondary from '../../components/shared/Button/SecondaryButton';
import ButtonGradient, {
  hasLeadingImageFieldContent,
} from '../../components/shared/Button/GradientButton';
import ButtonDiscreet from '../../components/shared/Button/ButtonDiscreet';
import UiBox from '../../components/shared/Box';
import UiHeading from '../../components/shared/Heading';
import useApplyV2Theme from '../../lib/hooks/useApplyV2Theme';
import { JSSLink } from '../../components/shared/Links/JSSLink.styles';

/** When CTA link has no href or no hash, match legacy TA hero scroll target. */
const TA_HERO_DEFAULT_SCROLL_SECTION_ID = 'pre-screener-header-banner';

const TAHeroBanner = (props: TAHeroBannerProps): JSX.Element => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const isTablet = useMediaQuery(theme.breakpoints.between(500, 'lg'));
  const isMobile = useMediaQuery(theme.breakpoints.between('xs', 500));
  // const isNotificationBannerOpen = useSelector(
  //   (state: RootState) => state.config.isNotificationBannerOpen
  // );
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const { isToapplyV2Theme } = useApplyV2Theme();
  if (!props?.fields) return <></>;
  const bannerProps = props.fields;

  const BannerImage = (): JSX.Element => {
    return (
      <NextImage
        priority={true}
        field={
          isMobile
            ? bannerProps?.mobileImage
            : isTablet
            ? bannerProps?.tabletImage
            : bannerProps?.imageDesktop
        }
        quality={100}
        style={{ zIndex: 0, objectFit: 'cover', objectPosition: 'center' }}
        fill
      ></NextImage>
    );
  };

  const SectionItems = (): JSX.Element => {
    return (
      <StBoxWrapper isMobile={isMobile} isTablet={isTablet}>
        <StTitle>
          {isToapplyV2Theme ? (
            <UiHeading
              size="h1"
              data-testid="hero-and-search-title"
              styles={{
                fontSize: bannerProps?.fontSize?.value || '32px',
                lineHeight: bannerProps?.lineHeight?.value || '45px',
                color: (theme.palette as any)?.fontTitleColor1?.main,
              }}
              isChildren={isToapplyV2Theme}
            >
              <RichText field={bannerProps?.title} />
            </UiHeading>
          ) : (
            <RichText field={bannerProps?.title}></RichText>
          )}
        </StTitle>
        <StRichTextGridItem isTablet={isTablet} isMobile={isMobile}>
          <RichText field={bannerProps?.content}></RichText>
        </StRichTextGridItem>
        {(() => {
          const ctaButton = bannerProps?.ctaButtons?.[0];
          const btnType = ctaButton?.fields?.buttonVariant?.value || ButtonType.CONTAINED;
          const enableAretiChatbotNavigation =
            ctaButton?.fields?.enableAretiChatbotNavigation?.value;
          let v = lowerCase(btnType);
          if (
            v !== ButtonType.CONTAINED &&
            v !== ButtonType.OUTLINED &&
            v !== ButtonType.GRADIENT &&
            v !== ButtonType.TEXT
          ) {
            v = ButtonType.CONTAINED;
          }
          const linkField = ctaButton?.fields?.link;
          const ctaButtonHref = linkField?.value?.href;
          const ctaLabel = linkField?.value?.text;
          const buttonImageField = ctaButton?.fields?.image;
          const hasLeadingImage = hasLeadingImageFieldContent(buttonImageField);
          const ctaBoxStyles = {
            alignSelf: isTablet || isMobile ? 'center' : 'none',
            marginBottom: isTablet || isMobile ? '100px' : '0px',
            [theme.breakpoints.between('xs', 500)]: {
              lineHeight: '2.5rem',
            },
          };
          const jssLinkSx = {
            alignSelf: isTablet || isMobile ? 'center' : 'none',
            marginBottom: isTablet || isMobile ? '100px' : '0px',
            [theme.breakpoints.between('xs', 500)]: {
              lineHeight: '2.5rem',
            },
          };

          if (isToapplyV2Theme) {
            return (
              <UiBox styles={ctaBoxStyles}>
                {v === ButtonType.CONTAINED && (
                  <ButtonPrimary
                    type="submit"
                    fullWidth
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(ctaButtonHref);
                    }}
                  >
                    {ctaLabel}
                  </ButtonPrimary>
                )}
                {v === ButtonType.OUTLINED && (
                  <ButtonSecondary
                    type="submit"
                    fullWidth
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(ctaButtonHref);
                    }}
                  >
                    {ctaLabel}
                  </ButtonSecondary>
                )}
                {v === ButtonType.TEXT && (
                  <ButtonDiscreet
                    type="submit"
                    fullWidth
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(ctaButtonHref);
                    }}
                  >
                    {ctaLabel}
                  </ButtonDiscreet>
                )}
                {v === ButtonType.GRADIENT && (
                  <ButtonGradient
                    type="submit"
                    fullWidth={!hasLeadingImage}
                    leadingImageField={buttonImageField}
                    onClick={(e) => {
                      e.preventDefault();
                      if (enableAretiChatbotNavigation) {
                        scrollToAreti();
                      } else {
                        scrollGradientCtaToSection(ctaButtonHref);
                      }
                    }}
                  >
                    {ctaLabel}
                  </ButtonGradient>
                )}
              </UiBox>
            );
          }

          // Legacy (non-V2): gradient uses ButtonGradient (with optional CMS `image`); JSSLink has no leading image slot.
          if (v === ButtonType.GRADIENT) {
            return (
              <UiBox styles={ctaBoxStyles}>
                <ButtonGradient
                  type="submit"
                  fullWidth={!hasLeadingImage}
                  leadingImageField={buttonImageField}
                  onClick={(e) => {
                    e.preventDefault();
                    if (enableAretiChatbotNavigation) {
                      scrollToAreti();
                    } else {
                      scrollGradientCtaToSection(ctaButtonHref);
                    }
                  }}
                >
                  {ctaLabel}
                </ButtonGradient>
              </UiBox>
            );
          }

          const linkVariant =
            v === ButtonType.OUTLINED ? 'outlined' : v === ButtonType.TEXT ? 'text' : 'contained';

          return (
            <JSSLink
              key={ctaButton?.id}
              className={'ta-hero-cta-btn btn'}
              variant={linkVariant}
              field={linkField!}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(ctaButtonHref);
              }}
              sx={jssLinkSx}
            />
          );
        })()}
      </StBoxWrapper>
    );
  };
  const trackEvents = () => {
    cdpService.event(CDP_EVENTS.SCREENER_CTA_LINK_CLICKED, {
      tenantKey: siteSettings?.tenantKey?.value,
      language: sitecoreContext?.language,
    });
  };
  //debugger
  /** Anchor id from CTA `href` when present (DTP-style); otherwise legacy default section id. */
  const scrollToSection = (href: string | undefined) => {
    trackEvents();
    const fromHref = href?.split('#')[1];
    const itemId = fromHref || TA_HERO_DEFAULT_SCROLL_SECTION_ID;
    const itemOffset = document.getElementById(itemId)?.offsetTop;

    const isBannerEnabledFromSitecore = sitecoreContext?.isNotificationBannerOpen ?? true;
    const notificationBanner = document.getElementById('notification-banner');
    const isNotificationBannerOpen = isBannerEnabledFromSitecore && notificationBanner !== null;

    const margin = isNotificationBannerOpen ? 50 : 0;
    if (itemOffset) {
      window.scroll({
        top:
          itemOffset -
          (isNotificationBannerOpen === null ? 0 : margin) +
          (isNotificationBannerOpen ? 50 : 0),
        left: 0,
        behavior: 'smooth',
      });
    }
  };

  /**
   * Gradient CTA only: scroll using document Y so nested anchors (e.g. `#areti-widget`) work on desktop.
   * `offsetTop` in `scrollToSection` is relative to `offsetParent`, not the document.
   */
  const scrollGradientCtaToSection = (href: string | undefined) => {
    trackEvents();
    const fromHref = href?.split('#')[1];
    const itemId = fromHref || TA_HERO_DEFAULT_SCROLL_SECTION_ID;
    const el = document.getElementById(itemId);

    const isBannerEnabledFromSitecore = sitecoreContext?.isNotificationBannerOpen ?? true;
    const notificationBanner = document.getElementById('notification-banner');
    const isNotificationBannerOpen = isBannerEnabledFromSitecore && notificationBanner !== null;

    const margin = isNotificationBannerOpen ? 50 : 0;
    if (el) {
      const targetDocumentY =
        el.getBoundingClientRect().top + (window.scrollY || window.pageYOffset);
      window.scroll({
        top:
          targetDocumentY -
          (isNotificationBannerOpen === null ? 0 : margin) +
          (isNotificationBannerOpen ? 50 : 0),
        left: 0,
        behavior: 'smooth',
      });
    }
  };

  const scrollToAreti = () => {
    trackEvents();
    if (
      window &&
      (window as any).Areti &&
      typeof (window as any).Areti.scrollIntoView === 'function'
    ) {
      (window as any).Areti.scrollIntoView({ block: 'center' });
    }
  };

  return (
    <>
      {!isDesktop && (
        <div id="hero-banner">
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              overflow: isMobile || isTablet ? 'hidden' : 'unset',
            }}
          >
            <StEllipseBox isMobile={isMobile} isTablet={isTablet} />
            <SectionItems />
            <BannerImage />
          </Box>
        </div>
      )}
      {isDesktop && (
        <StBannerWrapper id="hero-banner">
          <BannerImage />
          <StBannerContainer id="ta-hero-content" container>
            <Stack>
              <StBannerContent item xs={12} sm={12} md={12} lg={12}>
                <Grid
                  item
                  sx={{
                    position: 'relative',
                    [theme.breakpoints.down('sm')]: {
                      maxWidth: '450px',
                    },
                  }}
                >
                  <StEllipseBox isMobile={isMobile} isTablet={isTablet} />
                  <SectionItems />
                </Grid>
              </StBannerContent>
            </Stack>
          </StBannerContainer>
        </StBannerWrapper>
      )}
    </>
  );
};

export default TAHeroBanner;
